import React from 'react';
import { Box, Typography } from '@mui/material';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';
import { DreamFrequencyData } from './types';
import { format, subDays, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { serenityColors } from '../../utils/serenityColors';
import { useTranslation } from 'react-i18next';

interface SerenityTrendsProps {
  data: DreamFrequencyData[];
  period: number; // 7 ou 30 jours
}

const SerenityTrends: React.FC<SerenityTrendsProps> = ({ data, period }) => {
  const { t } = useTranslation();
  
  // Date de référence pour le calcul de la période
  const today = startOfDay(new Date());
  
  // Prépare les données pour l'affichage du graphique
  const prepareData = () => {
    const result = [];
    for (let i = period - 1; i >= 0; i--) {
      const date = subDays(today, i);
      const formattedDate = format(date, 'dd/MM', { locale: fr });
      const dayData = data.find(d => format(d.date, 'dd/MM') === formattedDate);
      const score = dayData?.serenityScore || 0;
      
      // On n'ajoute que les points avec un score > 0
      if (score > 0) {
        result.push({
          date: date.toISOString(), // Stocke la date au format ISO
          displayDate: formattedDate, // Garde la date formatée pour l'affichage
          score: score
        });
      }
    }
    return result;
  };

  const chartData = prepareData();

  // Calcule la moyenne en ignorant les jours sans données (score = 0)
  const calculateAverage = (data: Array<{ score: number }>) => {
    const validScores = data.filter(item => item.score > 0);
    if (!validScores || validScores.length === 0) return "0.0";
    const sum = validScores.reduce((acc, curr) => acc + curr.score, 0);
    return (sum / validScores.length).toFixed(1);
  };

  const average = parseFloat(calculateAverage(chartData));
  // Convertit la moyenne (0-100) en degrés (0-360) pour l'arc du cercle
  const circlePercentage = (average / 100) * 360;

  return (
    <Box sx={{ 
      width: '100%',
      height: '300px',
      position: 'relative'
    }}>
      {/* Titre 
      <Typography variant="h6" sx={{ 
        textAlign: 'center',
        color: 'white',
        mb: 2
      }}>
        {t('statistics.serenityTrends.title')}
      </Typography>*/}
      <Typography variant="subtitle1" sx={{ 
        textAlign: 'center',
        color: 'rgba(255, 255, 255, 0.7)',
        mt: -2
      }}>
        {t('statistics.serenityTrends.subtitle')}
      </Typography>

      {/* Indicateur de moyenne avec arc de cercle */}
      <Box sx={{ 
        position: 'absolute',
        top: '230px',
        right: 'calc(50% - 70px)',
        width: '120px',
        height: '120px',
        zIndex: 1
      }}>
        <svg width="120" height="120" viewBox="-10 -10 140 140">
          <defs>
            <linearGradient id="gradientArc" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={serenityColors.serene} />
              <stop offset="100%" stopColor={serenityColors.neutral} />
            </linearGradient>
          </defs>
          {/* Cercle de fond */}
          <circle
            cx="60"
            cy="60"
            r="50"
            fill="none"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="30"
            strokeLinecap="round"
          />
          {/* Arc coloré représentant la moyenne */}
          <path
            d={`M 60 10 A 50 50 0 ${circlePercentage > 180 ? 1 : 0} 1 ${
              60 + 50 * Math.sin((circlePercentage * Math.PI) / 180)
            } ${60 - 50 * Math.cos((circlePercentage * Math.PI) / 180)}`}
            fill="none"
            stroke="url(#gradientArc)"
            strokeWidth="20"
            strokeLinecap="round"
          />
          {/* Valeur de la moyenne */}
          <text
            x="60"
            y="55"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="white"
            fontSize="24"
            fontWeight="bold"
          >
            {average}
          </text>
          {/* Label "Moyenne" */}
          <text
            x="60"
            y="80"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="rgba(255,255,255,0.7)"
            fontSize="16"
          >
            {t('statistics.serenityTrends.average')}
          </text>
        </svg>
      </Box>

      {/* Graphique */}
      <ResponsiveContainer width="100%" height="100%" >
        <LineChart data={chartData} margin={{ top: 20, right: 20, bottom: 10, left: -20 }}>
          <defs>
            <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={serenityColors.serene}/>
              <stop offset="100%" stopColor={serenityColors.neutral}/>
            </linearGradient>
          </defs>
          
          {/* Ligne principale avec dégradé */}
          <Line 
            type="natural"
            dataKey="score" 
            stroke="url(#lineGradient)"
            strokeWidth={6}
            strokeLinecap="round"
            dot={{ 
              stroke: '#ffdd1f', 
              strokeWidth: 1, 
              r: 6, 
              fill: '#1A1B35' 
            }}
          />
          
          <XAxis 
            dataKey="displayDate" 
            stroke="rgba(255, 255, 255, 0.5)"
            tick={{ fill: 'transparent', fontSize: 11 }}
            interval={0}
            ticks={Array.from({ length: period }, (_, i) => {
              const date = subDays(today, period - 1 - i);
              return format(date, 'dd/MM', { locale: fr });
            })}
          />
          
          <YAxis 
            stroke="rgba(255, 255, 255, 0.5)"
            tick={{ fill: 'rgba(255, 255, 255, 0.7)', fontSize: 12 }}
            domain={[0, 100]}
            ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
            interval={1}
          />
          
          <Tooltip 
            contentStyle={{ 
              background: 'rgba(26, 27, 53, 0.9)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              color: 'white'
            }}
            formatter={(value: number) => [
              `${value}`,
              `${t('statistics.serenityTrends.popover.score')}`
            ]}
            labelFormatter={(label: string, payload: any) => {
              if (payload && payload[0]) {
                return format(new Date(payload[0].payload.date), t('statistics.serenityTrends.popover.dateFormat'));
              }
              return label;
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default SerenityTrends;
